import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { Logo } from "../components/Logo";
import { SHOW_FEED } from "../config/site";

const LINKS = [
  { label: "Capabilities", href: "/#capabilities" },
  { label: "Work", href: "/#work" },
  { label: "Print", href: "/print" },
  { label: "Events", href: "/events" },
  { label: "Feed", href: "/feed" },
  { label: "Process", href: "/#process" },
  { label: "Studio", href: "/#studio" },
].filter((l) => SHOW_FEED || l.href !== "/feed");

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <header
        data-testid="header-navigation"
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
          scrolled
            ? "border-b border-white/10 bg-ink/80 backdrop-blur-xl"
            : "border-b border-transparent bg-transparent"
        }`}
      >
        <div className="mx-auto flex h-16 lg:h-20 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Logo />
          <nav className="hidden items-center gap-8 lg:flex" aria-label="Primary">
            {LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className="group relative font-mono text-[11px] uppercase tracking-[0.25em] text-paper/70 transition-colors duration-300 hover:text-paper"
              >
                {l.label}
                <span className="absolute -bottom-1 left-0 h-px w-0 bg-acid transition-all duration-300 group-hover:w-full" />
              </a>
            ))}
            <a
              href="/#contact"
              data-testid="header-cta-start-project"
              className="group flex items-center gap-2 bg-acid px-5 py-2.5 font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:bg-paper"
            >
              Start a Project
              <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </nav>
          <button
            type="button"
            data-testid="mobile-menu-button"
            onClick={() => setOpen(true)}
            className="flex h-10 w-10 items-center justify-center border border-white/15 text-paper lg:hidden"
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5" />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[80] flex flex-col bg-ink"
          >
            <div className="flex h-16 items-center justify-between px-4">
              <Logo />
              <button
                type="button"
                data-testid="mobile-menu-close"
                onClick={() => setOpen(false)}
                className="flex h-10 w-10 items-center justify-center border border-white/15 text-paper"
                aria-label="Close menu"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center gap-2 px-6" aria-label="Mobile">
              {LINKS.map((l, i) => (
                <motion.a
                  key={l.href}
                  href={l.href}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                  onClick={() => setOpen(false)}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.4 }}
                  className="border-b border-white/10 py-5 font-display text-3xl font-extrabold uppercase tracking-tight text-paper transition-colors hover:text-acid"
                >
                  {l.label}
                </motion.a>
              ))}
              <motion.a
                href="/#contact"
                data-testid="mobile-cta-start-project"
                initial={{ opacity: 0, x: -24 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.35, duration: 0.4 }}
                className="mt-8 flex w-fit items-center gap-2 bg-acid px-8 py-4 font-mono text-xs font-bold uppercase tracking-[0.2em] text-ink"
              >
                Start a Project
                <ArrowUpRight className="h-4 w-4" />
              </motion.a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
